import { whenUpcartReady } from '@theme/upcart-runtime';

/**
 * Keeps UpCart's drawer from offering more of a product than its per-order limit (`max-per-order`).
 *
 * UpCart V2 exposes no variant or product id in its markup, so its own per-product CSS recipe
 * doesn't apply; the title link's `/products/<handle>` is the only identity a line carries. A line
 * at a limit of one loses its stepper entirely. Any other line at its limit keeps the stepper with
 * "+" disabled, so a cart that somehow got over the limit can still be stepped back down.
 *
 * UX only, like the theme's other caps: the checkout validation Function enforces the limit.
 */

const CART_ITEM_SELECTOR = '.upcart-public-cart-item';
const TITLE_LINK_SELECTOR = '.upcart-public-component-product-tile__title-link';
const QUANTITY_SELECTOR_SELECTOR = '.upcart-public-component-product-tile__quantity-selector';
const QUANTITY_PLUS_SELECTOR = '.upcart-public-component-product-tile__quantity-plus';

/** @type {Record<string, number>} product handle → limit, for the limited products in the cart */
let maxPerOrderByHandle = {};

// Cart updates can land in quick succession; a slower earlier response must not win.
let latestRequest = 0;

/** @param {Element} cartItem */
function productHandleOfCartItem(cartItem) {
  const link = cartItem.querySelector(TITLE_LINK_SELECTOR);
  if (!(link instanceof HTMLAnchorElement)) return null;

  return new URL(link.href).pathname.match(/\/products\/([^/]+)/)?.[1] ?? null;
}

/** @param {Element} cartItem */
function quantityOfCartItem(cartItem) {
  const input = cartItem.querySelector(`${QUANTITY_SELECTOR_SELECTOR} input`);
  return input instanceof HTMLInputElement ? Number(input.value) || 0 : 0;
}

/**
 * Sets every line's state from scratch, both ways. UpCart sometimes swaps line markup and
 * sometimes patches it in place (a quantity change keeps the same nodes), so a line can still
 * carry what an earlier pass set for a different quantity or even a different product.
 *
 * @param {ShadowRoot} shadowRoot
 */
function applyMaxPerOrderToDrawer(shadowRoot) {
  const cartItems = [...shadowRoot.querySelectorAll(CART_ITEM_SELECTOR)].map((cartItem) => ({
    cartItem,
    handle: productHandleOfCartItem(cartItem),
  }));

  // A product split across lines (one-time and subscription) shares one limit.
  /** @type {Map<string, number>} */
  const quantityByHandle = new Map();
  for (const { cartItem, handle } of cartItems) {
    if (handle) quantityByHandle.set(handle, (quantityByHandle.get(handle) ?? 0) + quantityOfCartItem(cartItem));
  }

  for (const { cartItem, handle } of cartItems) {
    const limit = handle ? maxPerOrderByHandle[handle] : undefined;
    const quantity = handle ? quantityByHandle.get(handle) ?? 0 : 0;
    const atLimit = limit !== undefined && quantity >= limit;
    const hideStepper = limit === 1 && quantity <= 1;

    const quantitySelector = cartItem.querySelector(QUANTITY_SELECTOR_SELECTOR);
    if (quantitySelector instanceof HTMLElement) quantitySelector.style.display = hideStepper ? 'none' : '';

    // UpCart disables "+" itself for its own reasons (stock, a pending update). The marker keeps
    // this from re-enabling a button it didn't disable.
    const plus = cartItem.querySelector(QUANTITY_PLUS_SELECTOR);
    if (!(plus instanceof HTMLButtonElement)) continue;
    if (atLimit) {
      if (!plus.disabled) {
        plus.disabled = true;
        plus.dataset.maxPerOrderDisabled = '';
      }
    } else if (plus.dataset.maxPerOrderDisabled !== undefined) {
      plus.disabled = false;
      delete plus.dataset.maxPerOrderDisabled;
    }
  }
}

/** @param {ShadowRoot} shadowRoot */
async function refreshMaxPerOrder(shadowRoot) {
  const request = ++latestRequest;

  try {
    const response = await fetch(`${Theme.routes.cart_url}?view=max-per-order`);
    if (!response.ok) throw new Error(`cart?view=max-per-order responded ${response.status}`);

    const limits = await response.json();
    if (request !== latestRequest) return;

    maxPerOrderByHandle = limits;
    applyMaxPerOrderToDrawer(shadowRoot);
  } catch (error) {
    // The drawer keeps UpCart's unrestricted stepper; checkout still refuses an over-limit order.
    console.warn('[upcart-max-per-order] Could not read per-order limits; the cart drawer is uncapped.', error);
  }
}

function init() {
  const shadowRoot = /** @type {ShadowRoot} */ (window.upcartDocumentOrShadowRoot);

  // UpCart re-renders lines without always announcing it (opening the drawer, toggling a plan), so
  // watch the markup rather than its events. Only childList is observed: the style and `disabled`
  // changes made here are attribute changes and can't retrigger this.
  new MutationObserver(() => applyMaxPerOrderToDrawer(shadowRoot)).observe(shadowRoot, {
    childList: true,
    subtree: true,
  });

  window.upcartSubscribeCartUpdated?.(() => refreshMaxPerOrder(shadowRoot));
  refreshMaxPerOrder(shadowRoot);
}

whenUpcartReady(
  () =>
    window.upcartDocumentOrShadowRoot instanceof ShadowRoot && typeof window.upcartSubscribeCartUpdated === 'function',
  init,
  '[upcart-max-per-order] UpCart runtime hooks never appeared; its cart drawer will offer more than a ' +
    "product's per-order limit. Check whether the app changed its API (upcartDocumentOrShadowRoot / upcartSubscribeCartUpdated)."
);
